import {useState, useCallback} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import BulkAssignMutation from '../../../../../mutations/ticketing/tickets/bulkAssignMutation';
import type {bulkAssignMutation} from '../../../../../mutations/ticketing/tickets/__generated__/bulkAssignMutation.graphql';

export interface BulkAssignResult {
	ticketId: string;
	success: boolean;
	error?: string;
}

type BulkAssignStatus = 'idle' | 'loading' | 'done';

export function useBulkAssign() {
	const [status, setStatus] = useState<BulkAssignStatus>('idle');
	const [results, setResults] = useState<BulkAssignResult[]>([]);

	const assign = useCallback((ticketIds: string[], assigneeId: string | null) => {
		setStatus('loading');
		setResults([]);

		commitTicketingMutation(
			BulkAssignMutation,
			{ticketIds, assigneeId},
			(response: bulkAssignMutation['response']) => {
				const payload = response.bulkAssignTickets;
				const updatedIds = new Set((payload?.updated ?? []).map(t => t.id));
				const failedIds = new Set(payload?.failed ?? []);
				const derived: BulkAssignResult[] = ticketIds.map(id => ({
					ticketId: id,
					success: updatedIds.has(id),
					error: failedIds.has(id) ? 'Failed to assign' : undefined,
				}));
				setResults(derived);
				setStatus('done');
			},
			() => {
				const derived: BulkAssignResult[] = ticketIds.map(id => ({
					ticketId: id,
					success: false,
					error: 'Failed to assign',
				}));
				setResults(derived);
				setStatus('done');
			}
		);
	}, []);

	const reset = useCallback(() => {
		setStatus('idle');
		setResults([]);
	}, []);

	return {status, results, assign, reset};
}
